import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Training, MonthlyTotal, GetTrainingsParams, UpdateTrainingStatusParams, UpdateTrainingNotesParams } from '@/types/trainings';
import { useCallback, useEffect } from 'react';
import { checkTrainingGoalProgress } from '@/utils/trainingGoalNotifications';
import { useI18n } from '@/contexts/I18nContext';


// Get user trainings for a specific month and year
export const useTrainings = (params: GetTrainingsParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['trainings', user?.id, params.month, params.year],
    queryFn: async (): Promise<Training[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_user_trainings_v6', {
          p_user_id: user.id,
          p_month: params.month,
          p_year: params.year
        });

      if (error) throw error;

      return data || [];
    },
    enabled: !!user,
  });
};

// Get monthly totals for a year
export const useMonthlyTotals = (year: number) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['monthly-totals', user?.id, year],
    queryFn: async (): Promise<MonthlyTotal[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_monthly_totals', {
          p_user_id: user.id,
          p_year: year
        });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });
};

// Get monthly classes count for a year
export const useMonthlyClasses = (year: number) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['monthly-classes', user?.id, year],
    queryFn: async (): Promise<MonthlyTotal[]> => {
      if (!user) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .rpc('get_monthly_totals_detailed', {
          p_user_id: user.id,
          p_year: year
        });
      if (error) throw error;
      // Transform the data to match MonthlyTotal interface
      return (data || []).map(item => ({
        month: item.month,
        completed_count: item.classes_count || 0
      }));
    },
    enabled: !!user,
  });
};

export const useMonthlyTrainings = (year: number) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['monthly-trainings', user?.id, year],
    queryFn: async (): Promise<MonthlyTotal[]> => {
      if (!user) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .rpc('get_monthly_totals_detailed', {
          p_user_id: user.id,
          p_year: year
        });
      if (error) throw error;
      // Transform the data to match MonthlyTotal interface
      return (data || []).map(item => ({
        month: item.month,
        completed_count: item.trainings_count || 0
      }));
    },
    enabled: !!user,
  });
};

// Update training status
export const useUpdateTrainingStatus = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useI18n();

  return useMutation({
    mutationFn: async ({ training_id, status }: UpdateTrainingStatusParams): Promise<boolean> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('update_training_status', {
          p_training_id: training_id,
          p_status: status,
          p_user_id: user.id
        });

      if (error) {
        throw error;
      }

      // SQL function returns false when no rows were affected (permission denied or not found)
      if (data === false) {
        throw new Error('Status update failed — you may not have permission to update this training');
      }

      return data;
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      
      // CRITICAL: Invalidate instructor-highlights when status changes
      // This ensures home page statistics update when classes are marked as Completed/Missed
      queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
      
      // CRITICAL: Invalidate practitioner-highlights when status changes
      // This ensures student home page statistics update when trainings are marked as Completed/Missed
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] });
      
      // Check training goal progress if status was updated to 'Completed'
      if (variables.status === 'Completed' && user?.id) {
        try {
          await checkTrainingGoalProgress(user.id, t);
        } catch (error) {
          // Silent fail
        }

      }
    },
  });
};

// Update training notes
export const useUpdateTrainingNotes = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ training_id, notes }: UpdateTrainingNotesParams): Promise<boolean> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('trainings')
        .update({ notes })
        .eq('id', training_id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return !!data;
    },
    onSuccess: () => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    },
  });
};

// Create new training
export const useCreateTraining = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (trainingData: Omit<Training, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      if (!user) throw new Error('User not authenticated');

      // CRITICAL: useCreateTraining is ONLY used by AddTrainingModal
      // AddTrainingModal ALWAYS creates trainings (is_class = false), never classes
      // Classes are created via AddClassModal which inserts directly into the database
      // Even if instructor_id is present, it means "training WITH an instructor", not a class
      
      // Create insertData WITHOUT is_class first, then explicitly set it to false
      const { is_class: _, ...trainingDataWithoutIsClass } = trainingData as any;
      const insertData = {
        ...trainingDataWithoutIsClass,
        user_id: user.id, // Always use the logged-in user's ID for trainings
        is_class: false, // CRITICAL: Always false for AddTrainingModal - never create classes here
      };

      const { data, error } = await supabase
        .from('trainings')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-trainings'] }); // CRITICAL: Invalidate monthly trainings count
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] }); // CRITICAL: Invalidate student statistics
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-classes'] });
    },
  });
};

// Update existing training
export const useUpdateTraining = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ trainingId, trainingData, instructorId }: { 
      trainingId: string; 
      trainingData: Partial<Omit<Training, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;
      instructorId?: string;
    }) => {
      if (!user) throw new Error('User not authenticated');

      // Update training directly via Supabase client
      const { data, error } = await supabase
        .from('trainings')
        .update(trainingData)
        .eq('id', trainingId)
        .eq('user_id', user.id) // Ensure user can only update their own trainings
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Handle instructor update if provided
      // Only update instructor if instructorId is explicitly provided (not undefined)
      // If instructorId is undefined, preserve existing instructor relationship
      if (instructorId !== undefined) {
        try {
          // Remove existing instructor relationship
          const { error: deleteError } = await supabase
            .from('training_instructors')
            .delete()
            .eq('training_id', trainingId);

          if (deleteError) {
            throw deleteError;
          }

          // Add new instructor relationship if provided (not null/empty)
          // instructorId can be null to remove instructor, or a valid UUID to set instructor
          if (instructorId && instructorId.trim() !== '') {
            const { error: insertError } = await supabase
              .from('training_instructors')
              .insert({
                training_id: trainingId,
                instructor_user_id: instructorId
              });

            if (insertError) {
              throw insertError;
            }
          }
        } catch (error) {
          throw error;
        }
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch trainings queries immediately
      // Use exact: false to invalidate all variations (different months/years)
      queryClient.invalidateQueries({ queryKey: ['trainings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['monthly-classes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'], exact: false });
      
      // Trigger immediate refetch for current queries
      queryClient.refetchQueries({ queryKey: ['trainings-with-notes'], exact: false });
    },
  });
}; 

// Get user trainings with notes and partners for a specific month and year
export const useTrainingsWithNotes = (params: GetTrainingsParams) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Bug 1 fix: Realtime subscription so a newly-approved class slot appears immediately
  // in the student's calendar without requiring a manual refresh.
  // We subscribe to INSERT/UPDATE on trainings where user_id = current user.
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`trainings-realtime-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trainings',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
          queryClient.invalidateQueries({ queryKey: ['trainings'] });
          queryClient.invalidateQueries({ queryKey: ['monthly-classes'] });
          queryClient.invalidateQueries({ queryKey: ['monthly-trainings'] });
          queryClient.invalidateQueries({ queryKey: ['all-trainings-for-totals'] });
          queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return useQuery({
    queryKey: ['trainings-with-notes', user?.id, params.month, params.year],
    queryFn: async (): Promise<{ trainings: Training[], notesData: Record<string, any> }> => {
      if (!user) throw new Error('User not authenticated');

      // Get trainings (including classes)
      const { data: trainings, error: trainingsError } = await supabase
        .rpc('get_user_trainings_including_classes', {
          p_user_id: user.id,
          p_month: params.month,
          p_year: params.year
        });

      if (trainingsError) throw trainingsError;

      // Map instructor_user_id to instructor_id for consistency
      // The SQL function returns instructor_user_id, but TypeScript expects instructor_id
      const mappedTrainings = (trainings || []).map((training: any) => ({
        ...training,
        instructor_id: training.instructor_user_id || training.instructor_id
      }));

      // Get session token for Edge Functions
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error('No access token');

      // Get notes and partners for all trainings (not just completed ones)
      const trainingIds = mappedTrainings?.map(t => t.id) || [];

      let notesData: Record<string, any> = {};

      if (trainingIds.length > 0) {
        try {
          // Use local Supabase functions URL in development
          const baseUrl = import.meta.env.DEV
            ? 'http://127.0.0.1:54321'
            : import.meta.env.VITE_SUPABASE_URL;

          const response = await fetch(`${baseUrl}/functions/v1/get_batch_training_notes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ training_ids: trainingIds }),
          });

          if (response.ok) {
            const result = await response.json();
            notesData = result.data || {};
          } else {
            console.warn('Failed to fetch batch training notes:', response.status);
          }
        } catch (error) {
          console.warn('Error fetching batch training notes:', error);
        }
      }

      return {
        trainings: mappedTrainings,
        notesData
      };
    },
    enabled: !!user,
    staleTime: 0, // Always consider data stale to allow refetch after invalidation
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: true, // Refetch when component mounts
  });
};

// Get user classes with notes and partners for a specific month and year
export const useClassesWithNotes = (params: GetTrainingsParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['classes-with-notes', user?.id, params.month, params.year],
    queryFn: async (): Promise<{ classes: Training[], notesData: Record<string, any> }> => {
      if (!user) throw new Error('User not authenticated');

      // Get classes
      const { data: classes, error: classesError } = await supabase
        .rpc('get_user_classes_only', {
          p_user_id: user.id,
          p_month: params.month,
          p_year: params.year
        });

      if (classesError) throw classesError;

      // Get session token for Edge Functions
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error('No access token');

      // Get notes and partners for all classes (not just completed ones)
      const classIds = classes?.map(c => c.id) || [];

      let notesData: Record<string, any> = {};

      if (classIds.length > 0) {
        try {
          // Use local Supabase functions URL in development
          const baseUrl = import.meta.env.DEV
            ? 'http://127.0.0.1:54321'
            : import.meta.env.VITE_SUPABASE_URL;

          const response = await fetch(`${baseUrl}/functions/v1/get_batch_training_notes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ training_ids: classIds }),
          });

          if (response.ok) {
            const result = await response.json();
            notesData = result.data || {};
          } else {
            console.warn('Failed to fetch batch class notes:', response.status);
          }
        } catch (error) {
          console.warn('Error fetching batch class notes:', error);
        }
      }

      return {
        classes: classes || [],
        notesData
      };
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}; 